import React, { useState } from "react";
import { getFaqItems } from "../data/faq-data";
import { useCountry } from "../contexts/CountryContext";

// Select the 10 most likely FAQs to be asked
const SELECTED_FAQS = [
  "what-is-optra",
  "how-long-eligibility",
  "approval-guarantee",
  "main-tools",
  "submission",
  "billing",
  "cancellation-refunds",
  "contact"
];
const MOST_ASKED_IDS = ["what-is-optra", "main-tools", "billing"];

/** Same questions, in the same order, in every region; getFaqItems() swaps country-specific wording. */
export default function FAQAccordion() {
  const { country } = useCountry();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Filter FAQs to only include the selected ones
  const displayedFAQs = getFaqItems(country).filter(faq => SELECTED_FAQS.includes(faq.id));
  const highlightEmail = (text) => {
    const email = "contact@optra-labs.com";
    if (!text || !text.includes(email)) return text;
    const parts = text.split(email);
    return (
      <>
        {parts[0]}
        <a href={`mailto:${email}`} data-inline-link="" style={{ color: "#1d4ed8", fontWeight: "inherit", display: "inline" }}>{email}</a>
        {parts.slice(1).join(email)}
      </>
    );
  };

  return (
    <section className='w-full flex flex-col items-center justify-center px-4 sm:px-6'>
      <div className='w-full max-w-5xl'>
        <div className='mb-10 flex flex-col items-center'>
          <span
            className="inline-flex items-center text-ui text-[10px] uppercase mb-4 px-2.5 py-1"
            style={{
              color: "#60A5FA",
              backgroundColor: "#FFFFFF",
              fontWeight: 500,
              letterSpacing: "0.08em",
              borderRadius: "8px",
              border: "1px solid rgba(96, 165, 250, 0.35)",
            }}
          >
            Support
          </span>
          <h2 
            className='text-emphasis text-[1.7rem] md:text-6xl lg:text-[3.75rem] leading-[1.08] text-center'
            style={{ 
              letterSpacing: '-0.022em',
              color: '#60A5FA',
              margin: 0,
              marginBottom: '1.75rem',
              fontWeight: 400,
            }}
          >
            Frequently Asked Questions
          </h2>
          <p
            className='text-ui text-sm md:text-lg leading-[1.7] max-w-2xl text-center w-full'
            style={{
              color: '#4B5563',
              margin: 0,
              fontWeight: 300,
              letterSpacing: '0.003em',
            }}
          >
            Answers to the questions we hear most.
          </p>
        </div>

        {/* Two columns from md up; each card opens independently within its column. */}
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start'>
          {displayedFAQs.map((faq, index) => (
            <div
              key={faq.id}
              className="bg-white/92 p-3.5 sm:p-4 rounded-2xl transition-all duration-300 border border-[#DCE8FB] shadow-[0_12px_30px_rgba(96,165,250,0.10)] hover:bg-white hover:border-[#C7DCF9]"
            >
              <button
                type="button"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-panel-${faq.id}`}
                aria-label={`Toggle answer for: ${faq.question}`}
                className="w-full text-left flex items-start justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#60A5FA]/55 focus-visible:ring-offset-2 rounded-lg"
              >
                <div className="flex-1 pr-2 min-w-0">
                  <span
                    className='block w-full text-left text-sm md:text-base min-w-0'
                    style={{
                      color: '#4B5563',
                      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
                      fontWeight: 300,
                    }}
                  >
                    {faq.question}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {faq.id === "contact" && (
                    <span
                      className="text-ui text-[9px] uppercase px-2 py-0.5 rounded-md hidden sm:inline-flex"
                      style={{
                        color: "#0C447C",
                        backgroundColor: "#EAF2FF",
                        fontWeight: 500,
                        letterSpacing: "0.06em",
                      }}
                    >
                      Contact
                    </span>
                  )}
                  {MOST_ASKED_IDS.includes(faq.id) && (
                    <span
                      className="text-ui text-[9px] uppercase px-2 py-0.5 rounded-md hidden sm:inline-flex"
                      style={{
                        color: "#0C447C",
                        backgroundColor: "#EAF2FF",
                        fontWeight: 500,
                        letterSpacing: "0.06em",
                      }}
                    >
                      Most asked
                    </span>
                  )}
                  <div 
                  className={`p-1 rounded transition-colors ${
                    openIndex === index 
                      ? 'bg-blue-50' 
                      : 'hover:bg-blue-50'
                  }`}
                  >
                    {openIndex === index ? (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ color: '#60A5FA' }}
                      >
                        <path d="M5 12h14"/>
                      </svg>
                    ) : (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ color: '#60A5FA' }}
                      >
                        <path d="M5 12h14"/>
                        <path d="M12 5v14"/>
                      </svg>
                    )}
                  </div>
                </div>
              </button>
              <div 
                id={`faq-panel-${faq.id}`}
                role="region"
                aria-label={`Answer: ${faq.question}`}
                className={`grid transition-all duration-300 ease-in-out ${
                  openIndex === index 
                    ? 'grid-rows-[1fr] opacity-100 mt-4' 
                    : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className='overflow-hidden'>
                  <p
                    className='text-sm md:text-base leading-relaxed'
                    style={{
                      color: '#4B5563',
                      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
                      fontWeight: 300,
                    }}
                  >
                    {highlightEmail(faq.answer)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
